import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Grid, InputAdornment, Stack, Typography } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DividerHeading } from '@ui2/structure'
import { SaveButton } from '@ui/actions'
import { Form, mergeDefaults, TextField, UnitConvertedField } from '@ui/forms'
import { enqueueSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import {
  getSimplifiedSeismicModelConfig,
  getSimplifiedSeismicModelInputs,
  getSimplifiedSeismicResults,
  getWindLoadSettings,
} from '@queries'
import { postSimplifiedSeismicConfig } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import useStoreysForSeismic from '../../hooks/useStoreysForSeismic'
import SeismicBaseParameters from './components/BaseParameters'
import SeismicDimensionsSceneMarkers from './components/SeismicDimensionsSceneMarkers'
import { FocusedDimension } from './components/SeismicDimensionsSceneMarkers/utils'
import { seismicModelConfig } from './schema'
import { getBuildingDimensionsFromWindLoadSettings } from './utils'

/** Keeps the storey caption from squeezing the inputs on a narrow drawer. */
const STOREY_LABEL_WIDTH = 46

const SimplifiedSeismicConfigForm = (): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()

  const queryClient = useQueryClient()

  // Which Länge/Breite field is focused, so only that storey's axis lights up.
  const [focusedDimension, setFocusedDimension] = useState<FocusedDimension | undefined>(undefined)

  const storeys = useStoreysForSeismic()

  const invalidateResultsQueries = useResultsInvalidation()

  const { data: windLoadSettings } = useQuery({
    queryKey: getWindLoadSettings.getKey(projectId),
    queryFn: () => getWindLoadSettings.request(projectId),
    onError: () => enqueueSnackbar('Fehler beim Laden der Dimensionen'),
    refetchOnMount: true,
  })

  const { data: seismicModelConfigData } = useQuery({
    queryKey: getSimplifiedSeismicModelConfig.getKey(projectId),
    queryFn: () => getSimplifiedSeismicModelConfig.request(projectId),
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim laden der Erdbebenkonfiguration'), {
        variant: 'error',
      })
    },
    refetchOnMount: true,
  })

  const { isLoading: isSaving, mutate } = useMutation(
    (data: SeismicSettings) => postSimplifiedSeismicConfig.request(projectId, data),
    {
      onSuccess: () => {
        invalidateResultsQueries(projectId as string)
        queryClient.invalidateQueries(getSimplifiedSeismicModelConfig.getKey(projectId))
        queryClient.invalidateQueries(getSimplifiedSeismicModelInputs.getKey(projectId))
        queryClient.invalidateQueries(getSimplifiedSeismicResults.getKey(projectId))
        enqueueSnackbar(
          'Erdbebenkonfiguration erfolgreich gespeichert und Modelldaten berechnet.',
          {
            variant: 'success',
          },
        )
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Erdbebenkonfiguration'),
          {
            variant: 'error',
          },
        )
      },
    },
  )

  const defaultValues = useMemo(() => {
    const schemaDefault = seismicModelConfig.getDefault()
    const dimensionsFromWindLoads = getBuildingDimensionsFromWindLoadSettings(windLoadSettings)

    if (!dimensionsFromWindLoads) {
      return mergeDefaults(seismicModelConfigData, schemaDefault)
    }

    const defaultsInitial = mergeDefaults(
      {
        // A fresh object per storey - a shared reference would let an edit in
        // one storey write through to all of them.
        building_dimensions: storeys.map(() => ({ ...dimensionsFromWindLoads })),
      },
      schemaDefault,
    )
    return mergeDefaults(seismicModelConfigData, defaultsInitial)
  }, [seismicModelConfigData, windLoadSettings, storeys])

  return (
    <Form
      validationSchema={seismicModelConfig}
      defaultValues={defaultValues}
      onSubmit={data => {
        mutate(data)
      }}
      enableReinitialize
    >
      <SeismicDimensionsSceneMarkers focused={focusedDimension} />

      <Typography fontSize={12} color="error" mb={0.5}>
        Stelle sicher, dass dein Tragwerk (Aussteifung und tragende Decken) fertig konfiguriert ist,
        da dies Einfluss auf die Ersatzkraftermittlung nimmt.
      </Typography>

      <DividerHeading spacingTop={0}>Grundparameter</DividerHeading>

      <SeismicBaseParameters />

      <DividerHeading>Geschossabmessungen</DividerHeading>

      <Stack direction="column" spacing={2}>
        {storeys.map(storey => (
          <Stack direction="row" spacing={1} alignItems="center" key={`dimensions-${storey}`}>
            <Typography fontSize={12} flexShrink={0} width={STOREY_LABEL_WIDTH}>
              {storeyToLabel(String(storey))}
            </Typography>
            <TextField
              inputLabel="Länge (X)"
              name={`building_dimensions.${storey}.x`}
              type="number"
              InputProps={{
                onFocus: () => setFocusedDimension({ storey, axis: 'x' }),
                onBlur: () => setFocusedDimension(undefined),
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
            />
            <TextField
              inputLabel="Breite (Y)"
              name={`building_dimensions.${storey}.y`}
              type="number"
              InputProps={{
                onFocus: () => setFocusedDimension({ storey, axis: 'y' }),
                onBlur: () => setFocusedDimension(undefined),
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
            />
            <TextField inputLabel="φ" name={`phi_values.${storey}`} type="number" />
          </Stack>
        ))}
      </Stack>

      <DividerHeading>Zusatzlasten</DividerHeading>

      <Grid container columnSpacing={1} rowSpacing={2}>
        <Grid item xs={4}>
          <UnitConvertedField
            inputLabel="Nutzlast"
            name="additional_slab_live_load"
            type="number"
            unitLabel={
              <span>
                kN/m<sup>2</sup>
              </span>
            }
          />
        </Grid>

        <Grid item xs={4}>
          <UnitConvertedField
            inputLabel="Schneelast"
            name="additional_roof_snow_load"
            type="number"
            unitLabel={
              <span>
                kN/m<sup>2</sup>
              </span>
            }
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            type="number"
            inputLabel={
              <span>
                ψ<sub>2</sub> Schnee
              </span>
            }
            name="psi_2_snow"
          />
        </Grid>

        <Grid item xs={8}>
          <UnitConvertedField
            inputLabel="Trennwandzuschlag (Erdbeben)"
            tooltip={{
              text: 'Zusätzliche Trennwandlast, für die Massenermittlung Erdbeben. Wird nicht in der Tragwerksmessung verwendet. Der Trennwandzuschlag aus der Stockwerksdefinition wird hier nicht verwendet. Die Wichten aller Wände werden bereits für die Massenermittlung berücksichtigt, entsprechend hiermit vorsichtig umgehen.',
            }}
            tooltipOnInputLabel
            name="additional_separating_wall_load"
            type="number"
            unitLabel={
              <span>
                kN/m<sup>2</sup>
              </span>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SaveButton loading={isSaving} type="submit" data-cy="save-simplified-seismic-config-btn">
            Speichern und Modellwerte ermitteln
          </SaveButton>
        </Grid>
      </Grid>
    </Form>
  )
}

export default SimplifiedSeismicConfigForm
